import { Flex, Popover, Skeleton } from 'antd';
import Button from 'components/Button';
import React, {
  ComponentProps,
  PropsWithChildren,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { LegendText } from '../../common/Legend';

import type { SeasonOverviewCommonWidgetFilters } from 'screens/season-overview/types';

import usePlantingGroupStagesQuery from 'screens/season-overview/hooks/usePlantingGroupStagesQuery';

interface PlantingGroupsPopoverViewProps {
  isLoading: boolean;
  legends: { key: string; value?: number }[];
  isPopoverOpen: boolean;
  setIsPopoverOpen: (open: boolean) => void;
  children: React.ReactNode;
}
interface PlantingGroupsPopoverProps
  extends PropsWithChildren,
    SeasonOverviewCommonWidgetFilters {
  jrAgronomistId?: string;
}

// Constants
const POPOVER_CLASS = 'season-overview__popover-content';
const SKELETON_COLOR = '#c5b4b452';

const PlantingGroupsPopover: React.FC<PlantingGroupsPopoverProps> = ({
  children,
  jrAgronomistId,
  country,
  seasonTypeId,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const params = useMemo(
    () => ({
      params: { country, seasonTypeId, juniorAgId: jrAgronomistId },
      enabled: isPopoverOpen && !!country && !!seasonTypeId && !!jrAgronomistId,
    }),
    [country, seasonTypeId, jrAgronomistId, isPopoverOpen],
  );

  const { data, isLoading } = usePlantingGroupStagesQuery(params);
  const { onboarding, planting, collections, closed } = data || {};

  const legends = useMemo(
    () => [
      { key: 'onboarding', value: onboarding },
      { key: 'planting', value: planting },
      { key: 'collections', value: collections },
      { key: 'closed', value: closed },
    ],
    [onboarding, planting, collections, closed],
  );

  if (!jrAgronomistId) return children;

  return (
    <PlantingGroupsPopoverView
      isLoading={isLoading}
      legends={legends}
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={setIsPopoverOpen}
    >
      {children}
    </PlantingGroupsPopoverView>
  );
};

const PlantingGroupsPopoverView: React.FC<PlantingGroupsPopoverViewProps> = ({
  isLoading,
  legends,
  isPopoverOpen,
  setIsPopoverOpen,
  children,
}) => {
  const { t } = useTranslation();
  const title = (
    <span className={POPOVER_CLASS}>
      {t('seasonOverview.widgets.plantingGroupStages.header')}
    </span>
  );

  const content = (
    <Flex vertical className={POPOVER_CLASS}>
      {isLoading ? (
        <PopoverContentSkeleton />
      ) : (
        legends.map(({ key, value = 0 }) => (
          <LegendItem
            key={key}
            legend={{
              legend: t(`seasonOverview.widgets.plantingGroupStages.${key}`),
              value: value || 0,
            }}
          />
        ))
      )}
    </Flex>
  );

  return (
    <Popover
      title={title}
      content={content}
      trigger="hover"
      color="#000"
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
    >
      <Button
        type="link"
        className="p-0 text-black"
        style={{ textAlign: 'left', textDecoration: 'none' }}
      >
        {children}
      </Button>
    </Popover>
  );
};

const LegendItem = ({ legend }: ComponentProps<typeof LegendText>) => (
  <LegendText legend={legend} dot={false} valueStyle={{ color: '#fff' }} />
);

const PopoverCountSkeleton = () => (
  <Flex justify="space-between" align="center" gap={8}>
    <Skeleton.Input
      active
      size="small"
      style={{ width: '100%', height: 20, backgroundColor: SKELETON_COLOR }}
    />
    <Skeleton.Avatar
      active
      size="small"
      shape="circle"
      style={{ backgroundColor: SKELETON_COLOR, height: 22, width: 22 }}
    />
  </Flex>
);

const PopoverContentSkeleton = () => (
  <Flex vertical className={POPOVER_CLASS} gap={8}>
    {Array.from({ length: 4 }, (_, i) => (
      <PopoverCountSkeleton key={i} />
    ))}
  </Flex>
);

export default PlantingGroupsPopover;
